import { useEffect, useRef, useState } from 'react'
import { useStore } from '../state/store'
import { ValueField, useFieldOps } from './ValueField'
import { pickDesignFile } from './exporters'
import { useT } from '../i18n'
import {
  IconBrand,
  IconChevronDown,
  IconCommand,
  IconDice,
  IconExport,
  IconGallery,
  IconHistory,
  IconImport,
  IconMoon,
  IconRedo,
  IconReset,
  IconSun,
  IconUndo,
} from './icons'

export function Toolbar() {
  const doc = useStore((s) => s.doc)
  const undo = useStore((s) => s.undo)
  const redo = useStore((s) => s.redo)
  const canUndo = useStore((s) => s.past.length > 0)
  const canRedo = useStore((s) => s.future.length > 0)
  const perfMode = useStore((s) => s.perfMode)
  const setPerfMode = useStore((s) => s.setPerfMode)
  const snap = useStore((s) => s.snap)
  const setSnap = useStore((s) => s.setSnap)
  const resetDocument = useStore((s) => s.resetDocument)
  const loadDocument = useStore((s) => s.loadDocument)
  const theme = useStore((s) => s.theme)
  const toggleTheme = useStore((s) => s.toggleTheme)
  const overlay = useStore((s) => s.overlay)
  const setOverlay = useStore((s) => s.setOverlay)
  const pushToast = useStore((s) => s.pushToast)
  const rememberSeed = useStore((s) => s.rememberSeed)

  const widthOps = useFieldOps('Set canvas width', (d, v) => {
    d.canvas.width = v
    if (d.boundary.kind === 'rect') d.boundary.width = Math.min(d.boundary.width, v)
  })
  const heightOps = useFieldOps('Set canvas height', (d, v) => {
    d.canvas.height = v
    if (d.boundary.kind === 'rect') d.boundary.height = Math.min(d.boundary.height, v)
  })

  const onImport = () =>
    pickDesignFile(
      (parsed) => {
        loadDocument(parsed, 'Import design')
        pushToast('Design imported', 'ok')
      },
      () => pushToast('Could not read a Ventholes design from that file', 'error'),
    )

  const isMac = typeof navigator !== 'undefined' && /Mac/.test(navigator.userAgent)
  const t = useT()

  return (
    <header className="toolbar">
      <button
        className="brand"
        onClick={() => setOverlay('about')}
        title="About Ventholes"
        aria-haspopup="dialog"
      >
        <IconBrand size={18} />
        Ventholes
        <span className="brand-version">v2</span>
      </button>

      <div className="toolbar-group" role="group" aria-label="Canvas size">
        <ValueField label="W" value={doc.canvas.width} min={5} max={2000} unit="mm" compact {...widthOps} />
        <ValueField label="H" value={doc.canvas.height} min={5} max={2000} unit="mm" compact {...heightOps} />
      </div>

      <SeedChip />

      <div className="toolbar-sep" />

      <div className="toolbar-group" role="group" aria-label="History">
        <button className="icon-btn quiet" disabled={!canUndo} onClick={undo} title="Undo (Ctrl+Z)" aria-label="Undo">
          <IconUndo />
        </button>
        <button className="icon-btn quiet" disabled={!canRedo} onClick={redo} title="Redo (Ctrl+Shift+Z)" aria-label="Redo">
          <IconRedo />
        </button>
        <button
          className={overlay === 'history' ? 'icon-btn quiet active' : 'icon-btn quiet'}
          onClick={() => setOverlay(overlay === 'history' ? null : 'history')}
          title="Undo history (H)"
          aria-label="Undo history"
          aria-pressed={overlay === 'history'}
        >
          <IconHistory />
        </button>
        <button
          className="icon-btn quiet"
          onClick={() => {
            rememberSeed(doc.layout.seed)
            resetDocument()
            pushToast('Design reset to defaults. Undo restores it.', 'info')
          }}
          title="Reset design to defaults (undoable)"
          aria-label="Reset design"
        >
          <IconReset />
        </button>
      </div>

      <div className="toolbar-spacer" />

      <label
        className="switch"
        title="Snap drags and placements to the center, thirds, quarters, boundary edges, and a 1 mm grid"
      >
        <input type="checkbox" checked={snap} onChange={(e) => setSnap(e.target.checked)} />
        {t('toolbar.snap', 'Snap')}
      </label>

      <label className="switch" title="Defer regeneration until a drag gesture ends">
        <input type="checkbox" checked={perfMode} onChange={(e) => setPerfMode(e.target.checked)} />
        {t('toolbar.performance', 'Performance')}
      </label>

      <div className="toolbar-sep" />

      <div className="toolbar-group">
        <button
          className="icon-btn quiet"
          onClick={toggleTheme}
          title={theme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'}
          aria-label="Toggle theme"
        >
          {theme === 'dark' ? <IconSun /> : <IconMoon />}
        </button>
        <button
          className="icon-btn quiet"
          onClick={() => setOverlay('palette')}
          title={`Command palette (${isMac ? 'Cmd' : 'Ctrl'}+K)`}
          aria-label="Command palette"
        >
          <IconCommand />
        </button>
        <button
          className={overlay === 'gallery' ? 'icon-btn active' : 'icon-btn'}
          onClick={() => setOverlay(overlay === 'gallery' ? null : 'gallery')}
          title="Preset gallery and recipes"
        >
          <IconGallery />
          {t('toolbar.presets', 'Presets')}
        </button>
        <button className="icon-btn" onClick={onImport} title="Load a design from JSON or an exported SVG">
          <IconImport />
          {t('toolbar.import', 'Import')}
        </button>
        <button className="icon-btn primary" onClick={() => setOverlay('export')} title="Export (E)">
          <IconExport />
          {t('toolbar.export', 'Export')}
        </button>
      </div>
    </header>
  )
}

function SeedChip() {
  const seed = useStore((s) => s.doc.layout.seed)
  const commit = useStore((s) => s.commit)
  const seedHistory = useStore((s) => s.seedHistory)
  const rememberSeed = useStore((s) => s.rememberSeed)
  const [text, setText] = useState(String(seed))
  const [menuOpen, setMenuOpen] = useState(false)
  const chipRef = useRef<HTMLDivElement>(null)

  useEffect(() => setText(String(seed)), [seed])

  // Close the seed menu on any outside click.
  useEffect(() => {
    if (!menuOpen) return
    const onDown = (e: PointerEvent) => {
      if (!chipRef.current?.contains(e.target as Node)) setMenuOpen(false)
    }
    window.addEventListener('pointerdown', onDown)
    return () => window.removeEventListener('pointerdown', onDown)
  }, [menuOpen])

  const setSeed = (v: number, label: string) => {
    const next = Math.max(1, Math.round(v))
    if (next === seed) return
    rememberSeed(seed)
    commit(label, (d) => {
      d.layout.seed = next
    })
  }

  const commitText = () => {
    const v = parseFloat(text)
    if (Number.isFinite(v)) setSeed(v, 'Set seed')
    else setText(String(seed))
  }

  return (
    <div className="seed-chip mono" ref={chipRef}>
      <span className="seed-label">Seed</span>
      <input
        value={text}
        aria-label="Seed"
        onChange={(e) => setText(e.target.value)}
        onBlur={commitText}
        onKeyDown={(e) => e.key === 'Enter' && (e.target as HTMLInputElement).blur()}
      />
      <button
        onClick={() => setSeed(Math.floor(Math.random() * 1e9) + 1, 'Shuffle seed')}
        title="Shuffle seed (R)"
        aria-label="Shuffle seed"
      >
        <IconDice size={14} />
      </button>
      <button
        onClick={() => setMenuOpen((o) => !o)}
        title="Recent seeds"
        aria-label="Recent seeds"
        aria-expanded={menuOpen}
      >
        <IconChevronDown size={12} />
      </button>
      {menuOpen && (
        <div className="seed-menu" role="menu">
          <div className="seed-menu-title">Recent seeds</div>
          {seedHistory.length === 0 && <p className="hint">Shuffle to collect seeds here.</p>}
          {seedHistory.map((s) => (
            <button
              key={s}
              role="menuitem"
              onClick={() => {
                setSeed(s, 'Restore seed')
                setMenuOpen(false)
              }}
            >
              {s}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
